import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  motion, AnimatePresence,
  useMotionValue, useSpring, useTransform, useMotionTemplate,
} from 'framer-motion';
import './Gallery.scss';

/* ── Data ───────────────────────────────────────────────────────── */
const GALLERY = [
  { id:1,  service:'Hair Styling', label:'Precision Cut & Style',   img:'https://images.unsplash.com/photo-1560066984-138daab9ada5?auto=format&fit=crop&w=800&q=80' },
  { id:2,  service:'Hair Styling', label:'Blow Dry Perfection',     img:'https://images.unsplash.com/photo-1562322140-8baeececf3df?auto=format&fit=crop&w=800&q=80' },
  { id:3,  service:'Hair Styling', label:'Sleek Transformation',    img:'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?auto=format&fit=crop&w=800&q=80' },
  { id:4,  service:'Hair Styling', label:'Bouncy Volume Curls',     img:'https://images.unsplash.com/photo-1605497788044-5a32c7078486?auto=format&fit=crop&w=800&q=80' },

  { id:5,  service:'Bridal',       label:'Bridal Glow Look',        img:'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=800&q=80' },
  { id:6,  service:'Bridal',       label:'HD Bridal Makeup',        img:'https://images.unsplash.com/photo-1583089892943-e02e5b017b6a?auto=format&fit=crop&w=800&q=80' },
  { id:7,  service:'Bridal',       label:'Wedding Ready Glam',      img:'https://images.unsplash.com/photo-1526947425960-945c6e72858f?auto=format&fit=crop&w=800&q=80' },
  { id:8,  service:'Bridal',       label:'Elegant Bridal Look',     img:'https://images.unsplash.com/photo-1535957998204-991b60a9d5ba?auto=format&fit=crop&w=800&q=80' },

  { id:9,  service:'Skin Care',    label:'Deep Cleanse Facial',     img:'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?auto=format&fit=crop&w=800&q=80' },
  { id:10, service:'Skin Care',    label:'Glow Skin Treatment',     img:'https://images.unsplash.com/photo-1512290923902-8a9f81dc236c?auto=format&fit=crop&w=800&q=80' },
  { id:11, service:'Skin Care',    label:'Tan Removal & Glow',      img:'https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?auto=format&fit=crop&w=800&q=80' },
  { id:12, service:'Skin Care',    label:'Anti-Aging Facial',       img:'https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?auto=format&fit=crop&w=800&q=80' },

  { id:13, service:'Spa',          label:'Luxury Spa Ritual',       img:'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?auto=format&fit=crop&w=800&q=80' },
  { id:14, service:'Spa',          label:'Full Body Massage',       img:'https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&w=800&q=80' },
  { id:15, service:'Spa',          label:'Manicure & Pedicure',     img:'https://images.unsplash.com/photo-1604654894610-df63bc536371?auto=format&fit=crop&w=800&q=80' },
  { id:16, service:'Spa',          label:'Relaxing Foot Spa',       img:'https://images.unsplash.com/photo-1519823551278-64ac92734fb1?auto=format&fit=crop&w=800&q=80' },

  { id:17, service:'Hair Color',   label:'Balayage Highlights',     img:'https://images.unsplash.com/photo-1527799820374-887729555db1?auto=format&fit=crop&w=800&q=80' },
  { id:18, service:'Hair Color',   label:'Rich Colour Treatment',   img:'https://images.unsplash.com/photo-1595497299756-e1b28e5a8b22?auto=format&fit=crop&w=800&q=80' },
  { id:19, service:'Hair Color',   label:'Ombre & Highlights',      img:'https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?auto=format&fit=crop&w=800&q=80' },
  { id:20, service:'Hair Color',   label:'Vibrant Colour Refresh',  img:'https://images.unsplash.com/photo-1481833761820-0509d3217039?auto=format&fit=crop&w=800&q=80' },
];

const TABS = ['All', 'Hair Styling', 'Bridal', 'Skin Care', 'Spa', 'Hair Color'];

const SERVICE_COLOR = {
  'Hair Styling': '#d678d0',
  'Bridal':       '#c9748a',
  'Skin Care':    '#a87dc4',
  'Spa':          '#5ba89a',
  'Hair Color':   '#e07060',
};

/* ── 3-D tilt card with mouse-tracking glare ────────────────────── */
const TiltCard = ({ children, className, onClick, tabIndex, 'aria-label': ariaLabel, onKeyDown }) => {
  const ref = useRef(null);

  const rawX = useMotionValue(0.5);
  const rawY = useMotionValue(0.5);

  const springCfg = { stiffness: 280, damping: 28, mass: 0.6 };
  const x = useSpring(rawX, springCfg);
  const y = useSpring(rawY, springCfg);

  const rotateX = useTransform(y, [0, 1], [ 10, -10]);
  const rotateY = useTransform(x, [0, 1], [-10,  10]);
  const glareX  = useTransform(x, [0, 1], [  0, 100]);
  const glareY  = useTransform(y, [0, 1], [  0, 100]);

  const glare = useMotionTemplate`radial-gradient(circle at ${glareX}% ${glareY}%, rgba(255,255,255,0.18) 0%, transparent 65%)`;

  const onMove = useCallback((e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    rawX.set((e.clientX - r.left) / r.width);
    rawY.set((e.clientY - r.top)  / r.height);
  }, [rawX, rawY]);

  const onLeave = useCallback(() => {
    rawX.set(0.5);
    rawY.set(0.5);
  }, [rawX, rawY]);

  return (
    <motion.div
      ref={ref}
      className={className}
      style={{ rotateX, rotateY, transformPerspective: 900, transformStyle: 'preserve-3d' }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onClick={onClick}
      tabIndex={tabIndex}
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
    >
      {children}
      {/* Glare layer */}
      <motion.div className="gallery__card-glare" style={{ background: glare }} />
    </motion.div>
  );
};

/* ── Word-by-word text reveal ────────────────────────────────────── */
const WordReveal = ({ text, className, delay = 0 }) => (
  <span className={className} aria-label={text}>
    {text.split(' ').map((word, i) => (
      <motion.span
        key={i}
        className="gallery__word"
        initial={{ opacity: 0, y: 28, rotateX: -35 }}
        whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
        viewport={{ once: true, amount: 0.6 }}
        transition={{ duration: 0.65, delay: delay + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
      >
        {word}{' '}
      </motion.span>
    ))}
  </span>
);

/* ── Card entrance variants ──────────────────────────────────────── */
const cardIn = {
  hidden: { opacity: 0, y: 50, scale: 0.88, rotateX: 12 },
  show: (i) => ({
    opacity: 1, y: 0, scale: 1, rotateX: 0,
    transition: { duration: 0.65, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] },
  }),
  exit: { opacity: 0, scale: 0.85, y: -20, transition: { duration: 0.25 } },
};

/* ── Tab entrance variants ───────────────────────────────────────── */
const tabIn = {
  hidden: { opacity: 0, y: 16 },
  show: (i) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.45, delay: 0.2 + i * 0.07, ease: [0.22, 1, 0.36, 1] },
  }),
};

/* ═══════════════════════════════════════════════════════════════════ */
const Gallery = () => {
  const [activeTab, setActiveTab] = useState('All');
  const [selected,  setSelected]  = useState(null);
  const [imgError,  setImgError]  = useState({});
  const [lbIdx,     setLbIdx]     = useState(0);

  const filtered = activeTab === 'All'
    ? GALLERY
    : GALLERY.filter(i => i.service === activeTab);

  /* Sync lightbox index when selected changes */
  useEffect(() => {
    if (selected) setLbIdx(GALLERY.findIndex(i => i.id === selected.id));
  }, [selected]);

  /* Keyboard nav + body scroll lock */
  useEffect(() => {
    if (!selected) return;
    const handler = (e) => {
      if (e.key === 'Escape')     setSelected(null);
      if (e.key === 'ArrowRight') setLbIdx(p => (p + 1) % GALLERY.length);
      if (e.key === 'ArrowLeft')  setLbIdx(p => (p - 1 + GALLERY.length) % GALLERY.length);
    };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', handler); document.body.style.overflow = ''; };
  }, [selected]);

  const handleImgError = (id) => setImgError(p => ({ ...p, [id]: true }));

  const countFor = (tab) => tab === 'All' ? GALLERY.length : GALLERY.filter(i => i.service === tab).length;

  return (
    <>
      <section className="gallery" aria-label="Service Gallery">

        {/* ── Animated background blobs ── */}
        <div className="gallery__blobs" aria-hidden="true">
          <div className="gallery__blob gallery__blob--1" />
          <div className="gallery__blob gallery__blob--2" />
          <div className="gallery__blob gallery__blob--3" />
        </div>

        {/* ── Header ── */}
        <div className="gallery__header">
          <motion.span
            className="gallery__kicker"
            initial={{ opacity: 0, letterSpacing: '0.5em' }}
            whileInView={{ opacity: 1, letterSpacing: '0.24em' }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            Our Portfolio
          </motion.span>

          <h2 className="gallery__title" style={{ perspective: 600 }}>
            <WordReveal text="Our Work Speaks" delay={0.1} />
            <WordReveal text="for Itself" delay={0.4} />
          </h2>

          <motion.p
            className="gallery__subtitle"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.7, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            Browse our transformations by service — each look is crafted with care, skill and love.
          </motion.p>
        </div>

        {/* ── Tab filters ── */}
        <div className="gallery__tabs" role="tablist">
          {TABS.map((tab, i) => (
            <motion.button
              key={tab}
              custom={i}
              variants={tabIn}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.5 }}
              role="tab"
              aria-selected={activeTab === tab}
              className={`gallery__tab${activeTab === tab ? ' gallery__tab--active' : ''}`}
              onClick={() => setActiveTab(tab)}
              whileTap={{ scale: 0.94 }}
            >
              <span className="gallery__tab-name">{tab}</span>
              <span className="gallery__tab-count">{countFor(tab)}</span>

              {activeTab === tab && (
                <motion.span
                  className="gallery__tab-bg"
                  layoutId="tab-bg"
                  transition={{ type: 'spring', stiffness: 420, damping: 30 }}
                />
              )}
            </motion.button>
          ))}
        </div>

        {/* ── Grid ── */}
        <div className="gallery__grid-wrap">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.div
              key={activeTab}
              className="gallery__grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {filtered.map((item, i) => (
                <motion.div
                  key={item.id}
                  custom={i}
                  variants={cardIn}
                  initial="hidden"
                  animate="show"
                  exit="exit"
                  layout
                  className="gallery__card-wrap"
                >
                  <TiltCard
                    className="gallery__card"
                    onClick={() => setSelected(item)}
                    tabIndex={0}
                    aria-label={`View ${item.label}`}
                    onKeyDown={(e) => e.key === 'Enter' && setSelected(item)}
                  >
                    {/* Image with inner zoom on hover */}
                    <motion.div
                      className="gallery__card-img"
                      whileHover={{ scale: 1.09 }}
                      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {imgError[item.id] ? (
                        <div
                          className="gallery__card-fallback"
                          style={{ background: `linear-gradient(135deg, ${SERVICE_COLOR[item.service]}44, ${SERVICE_COLOR[item.service]}bb)` }}
                        >
                          <span>{item.label}</span>
                        </div>
                      ) : (
                        <img src={item.img} alt={item.label} loading="lazy" onError={() => handleImgError(item.id)} />
                      )}
                    </motion.div>

                    {/* Tag pill */}
                    <motion.span
                      className="gallery__card-tag"
                      style={{ background: SERVICE_COLOR[item.service] }}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.07 + 0.3, duration: 0.4 }}
                    >
                      {item.service}
                    </motion.span>

                    {/* Bottom overlay — slides up on hover */}
                    <motion.div
                      className="gallery__card-overlay"
                      initial={{ y: '100%', opacity: 0 }}
                      whileHover={{ y: '0%', opacity: 1 }}
                      transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <span className="gallery__card-label">{item.label}</span>
                      <span className="gallery__card-open">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                        </svg>
                        Open
                      </span>
                    </motion.div>
                  </TiltCard>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* ── CTA ── */}
        <motion.div
          className="gallery__cta-wrap"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.7 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="gallery__cta-text">Love what you see?</p>
          <motion.a
            href="#contact"
            className="gallery__cta-btn"
            whileHover={{ scale: 1.04, y: -3 }}
            whileTap={{ scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
          >
            Book Your Appointment
            <motion.svg
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              animate={{ x: [0, 5, 0] }}
              transition={{ repeat: Infinity, duration: 1.6, ease: 'easeInOut' }}
            >
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </motion.svg>
          </motion.a>
        </motion.div>
      </section>

      {/* ════════════════ Lightbox ════════════════ */}
      <AnimatePresence>
        {selected && (
          <motion.div
            className="gallery-lb"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setSelected(null)}
          >
            {/* Main card */}
            <motion.div
              className="gallery-lb__card"
              key={GALLERY[lbIdx]?.id}
              initial={{ scale: 0.82, opacity: 0, rotateY: -8 }}
              animate={{ scale: 1,    opacity: 1, rotateY:  0 }}
              exit={{ scale: 0.82,    opacity: 0, rotateY:  8 }}
              transition={{ type: 'spring', stiffness: 300, damping: 26 }}
              onClick={(e) => e.stopPropagation()}
            >
              <motion.img
                key={lbIdx}
                src={GALLERY[lbIdx]?.img}
                alt={GALLERY[lbIdx]?.label}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              />

              <div className="gallery-lb__info">
                <motion.span
                  className="gallery-lb__tag"
                  style={{ background: SERVICE_COLOR[GALLERY[lbIdx]?.service] }}
                  key={`tag-${lbIdx}`}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.35, delay: 0.1 }}
                >
                  {GALLERY[lbIdx]?.service}
                </motion.span>
                <motion.span
                  className="gallery-lb__label"
                  key={`label-${lbIdx}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.15 }}
                >
                  {GALLERY[lbIdx]?.label}
                </motion.span>
                <span className="gallery-lb__counter">
                  {lbIdx + 1} / {GALLERY.length}
                </span>
              </div>

              {/* Close */}
              <motion.button
                className="gallery-lb__close"
                onClick={() => setSelected(null)}
                aria-label="Close"
                whileHover={{ rotate: 90, scale: 1.1, background: 'rgba(214,120,208,0.55)' }}
                transition={{ duration: 0.2 }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </motion.button>

              {/* Prev */}
              <motion.button
                className="gallery-lb__nav gallery-lb__nav--prev"
                aria-label="Previous"
                onClick={(e) => { e.stopPropagation(); setLbIdx(p => (p - 1 + GALLERY.length) % GALLERY.length); }}
                whileHover={{ x: -3, background: 'rgba(214,120,208,0.5)' }}
                whileTap={{ scale: 0.92 }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
              </motion.button>

              {/* Next */}
              <motion.button
                className="gallery-lb__nav gallery-lb__nav--next"
                aria-label="Next"
                onClick={(e) => { e.stopPropagation(); setLbIdx(p => (p + 1) % GALLERY.length); }}
                whileHover={{ x: 3, background: 'rgba(214,120,208,0.5)' }}
                whileTap={{ scale: 0.92 }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 18l6-6-6-6"/></svg>
              </motion.button>
            </motion.div>

            {/* Thumbnail strip */}
            <motion.div
              className="gallery-lb__thumbs"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              {GALLERY.map((item, i) => (
                <motion.button
                  key={item.id}
                  className={`gallery-lb__thumb${i === lbIdx ? ' gallery-lb__thumb--active' : ''}`}
                  onClick={() => setLbIdx(i)}
                  whileHover={{ scale: 1.12, y: -4 }}
                  whileTap={{ scale: 0.96 }}
                  animate={i === lbIdx ? { scale: 1.12, y: -4, outline: '2px solid #d678d0' } : { scale: 1, y: 0 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 22 }}
                >
                  <img src={item.img} alt={item.label} />
                </motion.button>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Gallery;
